import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import logger from '../lib/logger';
import { supabase, AuthUserWithProfile } from '../lib/supabase';
import { 
  signInWithEmail, 
  signOut, 
  getCurrentSession, 
  getCurrentUser, 
  getUserProfile, 
  getUserOrganization, 
  isEmailVerified,
  refreshSession, 
  onAuthStateChange, 
  DEMO_ORGANIZATION,
} from '../services/authService';
import { DEMO_CONFIG } from '../demo/demoUsers';

interface UseAuthReturn {
  user: AuthUserWithProfile | null;
  session: unknown | null;
  profile: any;
  organization: any;
  role: string;
  isAuthenticated: boolean;
  isEmailVerified: boolean;
  isLoading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  clearError: () => void;
}

export const useAuth = (): UseAuthReturn => {
  const [user, setUser] = useState<AuthUserWithProfile | null>(null);
  const [session, setSession] = useState<unknown | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const [organization, setOrganization] = useState<any>(null);
  
  const hasValidOrgRef = useRef(false);
  const userRef = useRef<AuthUserWithProfile | null>(null);
  const [role, setRole] = useState<string>('viewer');
  const [isEmailVerified, setIsEmailVerified] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const setUserWithRef = (newUser: AuthUserWithProfile | null) => {
    userRef.current = newUser;
    setUser(newUser);
  };

  const fetchUserData = useCallback(async (userId: string, existingUser?: AuthUserWithProfile) => {
    try {
      console.log('[AUTH DEBUG] fetchUserData START', { userId, isDemo: userId?.startsWith('demo-') });
      
      const isDemoUser = userId.startsWith('demo-');
      
      if (isDemoUser) {
        console.log('[AUTH DEBUG] fetchUserData DEMO USER - setting up');
        
        const demoUserWithOrg = {
          ...(existingUser || userRef.current),
          organization_id: DEMO_ORGANIZATION.id,
          organization: DEMO_ORGANIZATION,
        };
        
        setOrganization(DEMO_ORGANIZATION);
        setUserWithRef(demoUserWithOrg as AuthUserWithProfile);
        hasValidOrgRef.current = true;
        
        const profileData = await getUserProfile(userId);
        setProfile(profileData);
        
        const userRole = profileData?.role || 'viewer';
        setRole(userRole);
        setIsEmailVerified(true);
        
        console.log('[AUTH DEBUG] fetchUserData DEMO COMPLETE', { hasOrganization: true, role: userRole });
        
        // CRITICAL: Set loading to false for demo users!
        setIsLoading(false);
        return;
      }
      
      // Normal flow for non-demo users
      const [profileData, organizationData, emailVerified] = await Promise.all([
        getUserProfile(userId),
        getUserOrganization(userId),
        isEmailVerified(userId),
      ]);

      setProfile(profileData);
      
      if (organizationData) {
        setOrganization(organizationData);
        hasValidOrgRef.current = true;
      } else if (!hasValidOrgRef.current) {
        setOrganization(null);
      }
      
      setIsEmailVerified(emailVerified);
      
      const userRole = profileData?.role || 'viewer';
      setRole(userRole);
      
      if (existingUser && organizationData && !existingUser.organization) {
        setUserWithRef({
          ...existingUser,
          organization: organizationData,
        });
      }
    } catch (err) {
      logger.error('Fel vid hämtning av användardata', { error: String(err) });
      setRole('viewer');
      setIsEmailVerified(false);
    } finally {
      console.log('[AUTH DEBUG] fetchUserData FINALLY - setting loading false');
      setIsLoading(false);
    }
  }, []);


  // SAFETY TIMEOUT: Force loading to false after 3 seconds
  useEffect(() => {
    const forceTimeout = setTimeout(() => {
      console.warn('[AUTH SAFETY] Force setting isLoading to false after timeout');
      setIsLoading(false);
    }, 3000);
    
    return () => clearTimeout(forceTimeout);
  }, []);

  useEffect(() => {
    let mounted = true;
    let loadingTimeout: NodeJS.Timeout;

    const initializeAuth = async () => {
      try {
        console.log('[AUTH DEBUG] initializeAuth START');
        
        setIsLoading(true);
        setError(null);

        // Add timeout to prevent infinite loading
        loadingTimeout = setTimeout(() => {
          if (mounted) {
            console.warn('[AUTH DEBUG] initializeAuth TIMEOUT - forcing loading to false');
            setIsLoading(false);
          }
        }, 5000);
        
        const currentSession = await getCurrentSession();
        
        if (!mounted) return;

        clearTimeout(loadingTimeout);

        console.log('[AUTH DEBUG] initializeAuth session result', { hasSession: !!currentSession });

        if (currentSession) {
          setSession(currentSession);
          
          const currentUser = await getCurrentUser();
            
          if (!mounted) return;
            
          console.log('[AUTH DEBUG] initializeAuth user result', { hasUser: !!currentUser });
            
          if (currentUser) {
            const authUser: AuthUserWithProfile = {
              id: currentUser.id,
              email: currentUser.email || '',
              organization: currentUser.organization,
              profile: currentUser.profile,
              email_verified: currentUser.email_verified,
            };
             
            setUserWithRef(authUser);
            await fetchUserData(currentUser.id, authUser);
          } else {
            console.log('[AUTH DEBUG] initializeAuth NO USER - setting loading false');
            setIsLoading(false);
          }
        } else {
          console.log('[AUTH DEBUG] initializeAuth NO SESSION - setting loading false');
          setUserWithRef(null);
          setProfile(null);
          setOrganization(null);
          setRole('viewer');
          setIsEmailVerified(false);
          setIsLoading(false);
        }
      } catch (err) {
        if (!mounted) return;        
        logger.error('Fel vid initiering av auth', { error: String(err) });
        setError('Kunde inte hämta inloggningsstatus');
        setIsLoading(false);
      }
    };

    initializeAuth();

    const { data: { subscription } } = onAuthStateChange(async (event, session) => {
      if (!mounted) return;
      
      logger.audit('Auth state change', { event, hasSession: !!session });
      
      if (session) {
        setSession(session);
          
        const sessionUser = (session as any)?.user;
        if (sessionUser) {
          const authUser: AuthUserWithProfile = {
            id: sessionUser.id,
            email: sessionUser.email || '',
            organization: sessionUser.organization,
            profile: sessionUser.profile,
            email_verified: sessionUser.email_verified,
          };
            
          setUserWithRef(authUser);
          await fetchUserData(sessionUser.id, authUser);
        } else if (supabase) {
          const { data: { user: currentUser } } = await supabase.auth.getUser();
            
          if (currentUser) {
            const authUser: AuthUserWithProfile = {
              id: currentUser.id,
              email: currentUser.email || '',
            };
              
            setUserWithRef(authUser);
            await fetchUserData(currentUser.id);
          }
        }
      } else {
        if (user && user.id?.startsWith('demo-')) {
          logger.info('Demo user session ended, preserving demo state', {});
          return;
        }
        
        setUserWithRef(null);
        setSession(null);
        setProfile(null);
        setOrganization(null);
        setRole('viewer');
        setIsEmailVerified(false);
        setIsLoading(false);
      }
    });

    return () => {
      mounted = false;
      clearTimeout(loadingTimeout);
      subscription.unsubscribe();
    };
  }, [fetchUserData]);

  const handleSignIn = useCallback(async (email: string, password: string) => {
    try {
      console.log('[AUTH DEBUG] handleSignIn START', { email });
      
      setIsLoading(true);
      setError(null);

      const result = await signInWithEmail(email, password);
      
      console.log('[AUTH DEBUG] handleSignIn result', { success: result.success, hasUser: !!result.user });
      
      if (result.success && result.user) {
        setUserWithRef(result.user);
        setSession(await getCurrentSession());
        
        const isDemoUser = result.user.id?.startsWith('demo-');
        if (isDemoUser) {
          const userWithOrg = {
            ...result.user,
            organization_id: DEMO_ORGANIZATION.id,
            organization: DEMO_ORGANIZATION,
          };
          setUserWithRef(userWithOrg as AuthUserWithProfile);
          setOrganization(DEMO_ORGANIZATION);
          hasValidOrgRef.current = true;
        } else if (result.user.organization) {
          setOrganization(result.user.organization);
        }
        
        if (result.user.id) {
          await fetchUserData(result.user.id, result.user);
        }
        
        console.log('[AUTH DEBUG] handleSignIn COMPLETE', { isAuthenticated: !!user && !!session });
        
        return { success: true };
      } else {
        setError(result.error || 'Inloggning misslyckades');
        setIsLoading(false);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMsg = 'Oväntat fel vid inloggning';
      logger.error(errorMsg, { error: String(err) });
      setError(errorMsg);
      setIsLoading(false);
      return { success: false, error: errorMsg };
    }
  }, [fetchUserData]);

  const handleSignOut = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      await signOut();
      
      setUserWithRef(null);
      setSession(null);
      setProfile(null);
      setOrganization(null);
      setRole('viewer');
      setIsEmailVerified(false);
      hasValidOrgRef.current = false;
      
      logger.audit('Användare utloggad via useAuth', {});
    } catch (err) {
      logger.error('Fel vid utloggning', { error: String(err) });
      setError('Kunde inte logga ut');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleRefreshSession = useCallback(async () => {
    try {
      setIsLoading(true);
      const currentSession = await refreshSession();
        
      if (currentSession) {
        setSession(currentSession);
           
        const currentUser = await getCurrentUser();
        if (currentUser) {
          const authUser: AuthUserWithProfile = {
            id: currentUser.id,
            email: currentUser.email || '',
            organization: currentUser.organization,
            profile: currentUser.profile,
            email_verified: currentUser.email_verified,
          };
          setUserWithRef(authUser);
          await fetchUserData(currentUser.id, authUser);
        }
      }
    } catch (err) {
      logger.error('Fel vid uppdatering av session', { error: String(err) });
    } finally {
      setIsLoading(false);
    }
  }, [fetchUserData]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const isAuthenticated = useMemo(() => {
    const auth = !!user && !!session;
    console.log('[AUTH DEBUG] isAuthenticated computed', { 
      isAuthenticated: auth, 
      hasUser: !!user, 
      hasSession: !!session,
    });
    return auth;
  }, [user, session]);

  return {
    user,
    session,
    profile,
    organization,
    role,
    isAuthenticated,
    isEmailVerified,
    isLoading,
    error,
    signIn: handleSignIn,
    signOut: handleSignOut,
    refreshSession: handleRefreshSession,
    clearError,
  };
};

export default useAuth;
